
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  PhoneOutgoing, 
  CheckCircle2, 
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  AlertCircle,
  Zap,
  TrendingUp,
  RefreshCw
} from 'lucide-react';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area 
} from 'recharts';
import { checkApiHealth, sendCriticalAlert } from '../services/notificationService';
import { fetchAnalytics } from '../services/n8nService';
import {
  type CompletedCallRow,
  type QueueRow,
  isSuccessfulCompletedCall,
  completedCallStatusLabel,
  completedCallChipLine,
  completedTimestamp,
} from '../services/analyticsMappers';

// Default fallback data when analytics aren't available
const defaultChartData = [
  { name: 'Mon', calls: 45, success: 38 },
  { name: 'Tue', calls: 52, success: 44 },
  { name: 'Wed', calls: 38, success: 32 },
  { name: 'Thu', calls: 65, success: 59 },
  { name: 'Fri', calls: 48, success: 41 },
  { name: 'Sat', calls: 24, success: 18 },
  { name: 'Sun', calls: 15, success: 12 },
];

type QueueItem = QueueRow;
type CompletedCall = CompletedCallRow;

interface AnalyticsResponse {
  activeQueue: { data: QueueItem[] } | QueueItem[] | null;
  completedCalls: { data: CompletedCall[] } | CompletedCall[] | null;
  timestamp: string;
}

interface ParsedAnalytics {
  activeLeads: number;
  totalCalls: number;
  successRate: number;
  avgDuration: string;
  queueItems: QueueItem[];
  completedItems: CompletedCall[];
  lastUpdated: string;
}

// Helper to format relative time
const getRelativeTime = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

// Helper to format duration
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '0m 0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

// Parse the n8n analytics response
const parseAnalyticsResponse = (response: AnalyticsResponse[]): ParsedAnalytics => {
  // Combine data from all response items
  let queueItems: QueueItem[] = [];
  let completedItems: CompletedCall[] = [];
  let lastTimestamp = '';

  for (const item of response) {
    if (Array.isArray(item.activeQueue)) {
      queueItems = [...queueItems, ...item.activeQueue];
    } else if (item.activeQueue?.data) {
      queueItems = [...queueItems, ...item.activeQueue.data];
    }
    if (Array.isArray(item.completedCalls)) {
      completedItems = [...completedItems, ...item.completedCalls];
    } else if (item.completedCalls?.data) {
      completedItems = [...completedItems, ...item.completedCalls.data];
    }
    if (item.timestamp) {
      lastTimestamp = item.timestamp;
    }
  }

  // Filter out completed calls with null data (empty records)
  const validCompletedCalls = completedItems.filter(c => c.call_id !== null) as CompletedCallRow[];

  // Calculate stats
  const totalCalls = validCompletedCalls.length;
  const successfulCalls = validCompletedCalls.filter(c => isSuccessfulCompletedCall(c)).length;
  const successRate = totalCalls > 0 ? Math.round((successfulCalls / totalCalls) * 100) : 0;

  // Calculate average duration
  const durations = validCompletedCalls
    .map(c => c.total_duration_seconds)
    .filter((d): d is number => d !== null);
  const avgSeconds = durations.length > 0 
    ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) 
    : 0;

  return {
    activeLeads: queueItems.length,
    totalCalls,
    successRate,
    avgDuration: formatDuration(avgSeconds),
    queueItems,
    completedItems: validCompletedCalls,
    lastUpdated: lastTimestamp
  };
};

const MainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [systemHealth, setSystemHealth] = useState<'optimal' | 'degraded' | 'critical'>('optimal');
  const [isChecking, setIsChecking] = useState(false);
  const [analytics, setAnalytics] = useState<ParsedAnalytics | null>(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  // Fetch analytics from n8n
  const loadAnalytics = async () => {
    setIsLoadingAnalytics(true);
    setAnalyticsError(null);
    try {
      const rawData = await fetchAnalytics();
      // Parse the n8n response format
      const parsed = parseAnalyticsResponse(Array.isArray(rawData) ? rawData : [rawData]);
      setAnalytics(parsed);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setAnalyticsError('Failed to load analytics from n8n');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    // Load analytics on mount
    loadAnalytics();
    
    // Refresh analytics every 5 minutes
    const analyticsInterval = setInterval(loadAnalytics, 300000);
    
    return () => clearInterval(analyticsInterval);
  }, []);

  useEffect(() => {
    const runHealthCheck = async () => {
      setIsChecking(true);
      // Check the new n8n webhook URL
      const isHealthy = await checkApiHealth();
      const nextHealth = isHealthy ? 'optimal' : 'critical';
        
      if (nextHealth === 'critical' && systemHealth !== 'critical') {
        const settingsStr = localStorage.getItem('reachai_notification_settings');
        if (settingsStr) {
          const settings = JSON.parse(settingsStr);
          if (settings.alertOnHealthDecline) {
            await sendCriticalAlert(
              'Critical: ReachAI System Health Degraded',
              `The ReachAI API (analytics) is currently unreachable. Check VITE_API_BASE_URL and deployment.`
            );
          }
        }
      }
      setSystemHealth(nextHealth);
      setIsChecking(false);
    };

    runHealthCheck();
    const interval = setInterval(runHealthCheck, 300000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [systemHealth]);

  // Use analytics data if available, otherwise use defaults
  // Build chart data from analytics - showing queue vs completed over time
  const chartData = (() => {
    if (!analytics) return defaultChartData;
    
    // Create a simple summary chart based on current data
    // In production, n8n would send historical daily data
    const today = new Date();
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Generate last 7 days with current data on today
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(date.getDate() - (6 - i));
      const dayName = dayNames[date.getDay()];
      const isToday = i === 6;
      
      return {
        name: dayName,
        queued: isToday ? analytics.activeLeads : 0,
        completed: isToday ? analytics.totalCalls : 0,
        success: isToday ? Math.round(analytics.totalCalls * (analytics.successRate / 100)) : 0
      };
    });
  })();
  
  // Calculate saved hours estimate (assuming 5 min average manual call vs automated)
  const savedHours = analytics ? ((analytics.totalCalls * 5) / 60).toFixed(1) : '0';
  
  const stats = [
    { label: 'Total Calls', value: analytics?.totalCalls?.toString() || '0', change: '+0%', icon: PhoneOutgoing, iconWrap: 'p-2 rounded-lg bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 transition-colors group-hover:bg-indigo-600 group-hover:text-white' },
    { label: 'Active Queue', value: analytics?.activeLeads?.toString() || '0', change: '+0%', icon: Users, iconWrap: 'p-2 rounded-lg bg-blue-100 dark:bg-blue-950 text-blue-600 dark:text-blue-400 transition-colors group-hover:bg-blue-600 group-hover:text-white' },
    { label: 'Success Rate', value: analytics?.successRate ? `${analytics.successRate}%` : '0%', change: '+0%', icon: CheckCircle2, iconWrap: 'p-2 rounded-lg bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 transition-colors group-hover:bg-emerald-600 group-hover:text-white' },
    { label: 'Avg Call Duration', value: analytics?.avgDuration || '0m 0s', change: '+0%', icon: Clock, iconWrap: 'p-2 rounded-lg bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400 transition-colors group-hover:bg-amber-600 group-hover:text-white' },
    { label: 'Saved Manual Hours', value: `${savedHours}h`, change: '+0%', icon: Zap, iconWrap: 'p-2 rounded-lg bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400 transition-colors group-hover:bg-rose-600 group-hover:text-white' },
  ];

  // Build recent activity from queue items and completed calls
  const recentActivity = (() => {
    const items: { name: string; status: string; time: string; result: string }[] = [];
    
    // Add queue items (pending)
    if (analytics?.queueItems) {
      for (const item of analytics.queueItems.slice(0, 6)) {
        const q = item as QueueRow;
        items.push({
          name: q.name || 'Unknown Contact',
          status: q.status === 'pending' ? 'Queued' : q.status === 'processing' ? 'In-Progress' : q.status === 'dispatched' ? 'Dispatched' : q.status,
          time: getRelativeTime(q.created_at || q.createdAt || ''),
          result: q.attempts > 0 ? `Attempt ${q.attempts}` : 'Waiting'
        });
      }
    }
    
    // Add completed calls
    if (analytics?.completedItems) {
      for (const call of analytics.completedItems.slice(0, 6 - items.length)) {
        const c = call as CompletedCallRow;
        items.push({
          name: c.name || 'Unknown Contact',
          status: completedCallStatusLabel(c),
          time: getRelativeTime(completedTimestamp(c) || (c as any).createdAt || ''),
          result: completedCallChipLine(c)
        });
      }
    }
    
    // Return items or default fallback
    return items.length > 0 ? items : [
      { name: 'No activity yet', status: 'Waiting', time: 'Now', result: 'Queue empty' }
    ];
  })();

  return (
    <div className="space-y-8 animate-in fade-in duration-500 transition-colors duration-300">
      {/* Top Banner - System Health */}
      <div className={`p-4 rounded-3xl border transition-all duration-500 flex items-center justify-between ${
        systemHealth === 'optimal' 
          ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300' 
          : 'bg-rose-50 dark:bg-rose-950/20 border-rose-100 dark:border-rose-900 text-rose-800 dark:text-rose-300'
      }`}>
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-xl ${systemHealth === 'optimal' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
            {systemHealth === 'optimal' ? <Activity className="w-5 h-5 animate-pulse" /> : <AlertCircle className="w-5 h-5" />}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider opacity-70">ReachAI Gateway Status</p>
            <p className="font-bold">{systemHealth === 'optimal' ? 'All Systems Operational' : 'Gateway Connection Error'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="hidden md:flex flex-col items-end">
            <span className="text-[10px] uppercase font-bold opacity-60">Analytics</span>
            <span className="text-sm font-bold">{isLoadingAnalytics ? 'Loading...' : analyticsError ? 'Error' : 'Live'}</span>
          </div>
          <button 
            onClick={loadAnalytics}
            disabled={isLoadingAnalytics}
            className={`p-2 rounded-xl text-xs font-bold border transition-colors mr-2 ${
              systemHealth === 'optimal' 
                ? 'border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40' 
                : 'border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-900/40'
            }`}
            title="Refresh Analytics"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingAnalytics ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              void (async () => {
                setIsChecking(true);
                const ok = await checkApiHealth();
                setSystemHealth(ok ? 'optimal' : 'critical');
                setIsChecking(false);
              })();
            }}
            className={`px-4 py-2 rounded-xl text-xs font-bold border transition-colors ${
              systemHealth === 'optimal'
                ? 'border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40'
                : 'border-rose-200 dark:border-rose-800 hover:bg-rose-100 dark:hover:bg-rose-900/40'
            }`}
          >
            {isChecking ? 'Checking...' : 'Check now'}
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className={stat.iconWrap}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div className={`flex items-center text-xs font-bold ${stat.change.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {stat.change}
                {stat.change.startsWith('+') ? <ArrowUpRight className="w-3 h-3 ml-1" /> : <ArrowDownRight className="w-3 h-3 ml-1" />}
              </div>
            </div>
            <h3 className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-wider">{stat.label}</h3>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Call Volume Chart */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm transition-colors">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
                <TrendingUp className="w-4 h-4" />
              </div>
              <h3 className="font-bold text-slate-800 dark:text-slate-100">Queue & Call Activity</h3>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-indigo-500" />
                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">Queued</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500">Completed</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorQueued" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:opacity-10" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: '#1e293b',
                    color: '#f8fafc'
                  }}
                  itemStyle={{ color: '#f8fafc' }}
                  formatter={(value: number, name: string) => [value, name === 'queued' ? 'In Queue' : name === 'completed' ? 'Completed' : 'Successful']}
                />
                <Area type="monotone" dataKey="queued" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorQueued)" />
                <Area type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorCompleted)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-slate-800 dark:text-slate-100">Recent AI Interactions</h3>
            <button 
              onClick={() => navigate('/history')}
              className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 px-3 py-1 rounded-lg transition-colors"
            >
              View All History
            </button>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto pr-2 custom-scrollbar">
            {recentActivity.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border border-transparent hover:border-slate-100 dark:hover:border-slate-700">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                    {item.name[0]}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{item.name}</h4>
                    <span className="text-xs text-slate-400 dark:text-slate-500">{item.time}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs font-bold px-2 py-1 rounded-full inline-block ${
                    item.status === 'Completed' ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' : 
                    item.status === 'Failed' ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400' : 
                    item.status === 'Requeued' ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400' :
                    'bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400'
                  }`}>
                    {item.status}
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 uppercase tracking-wider font-bold">{item.result}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainDashboard;
